// Fichero de Alloggiati Web (Polizia di Stato, Italia) a partir de DNI y pasaportes
// extraídos por WhappScan (28-9-2026).
//
// Formato: «tracciato record» del manual oficial (MANUALEALBERGHI.pdf §12, enero 2022),
// variante SIN «ID appartamento»: una línea de 168 caracteres por huésped, campos
// alineados a la izquierda y rellenos de espacios, CR+LF entre líneas y NINGUNO tras la
// última, como mucho 1.000 líneas por fichero.
//
//   tipo(2) llegada(10) días(2) apellido(50) nombre(30) sexo(1) nacimiento(10)
//   comune nac.(9) provincia nac.(2) stato nac.(9) cittadinanza(9)
//   tipo doc.(5) número(20) lugar de expedición(9)
//
// Familiares (19) y miembros de grupo (20) van SIN documento (34 espacios) y justo
// después de su capofamiglia (17) o capogruppo (18). Nacido fuera de Italia: comune y
// provincia en blanco, stato siempre relleno. Lugar de expedición: código de comune si
// el documento es italiano, si no el del estado emisor.
//
// Las tablas (src/data/alloggiati) se pasan desde fuera para que la app solo las cargue
// cuando alguien genera el fichero.

export const TIPO = { single: "16", familyHead: "17", groupHead: "18", familyMember: "19", groupMember: "20" };
export const MAX_LINES = 1000;
const ITALIA = "100000100";
// Alemania, Austria, Suiza, Liechtenstein.
const GERMANIC = new Set(["100000216", "100000203", "100000241", "100000225"]);

// El portal rechaza letras acentuadas: mayúsculas A-Z sin tildes. Con `germanic`,
// Ä/Ö/Ü se transliteran AE/OE/UE como en la MRZ de los documentos alemanes,
// austriacos y suizos (ICAO 9303): el registro debe coincidir con el documento.
export function plain(value, { germanic = false } = {}) {
  let s = String(value || "");
  if (germanic) s = s.replace(/[Ää]/g, "AE").replace(/[Öö]/g, "OE").replace(/[Üü]/g, "UE");
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/ß/g, "SS")
    .replace(/[^A-Z0-9 '-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function pad(value, len) {
  return String(value || "").slice(0, len).padEnd(len, " ");
}

// «1983-08-12», «12.08.1983», «12/08/1983» → «12/08/1983».
export function toItalianDate(value) {
  const s = String(value || "").trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) return `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3]}`;
  return null;
}

function itDateToTime(value) {
  const m = String(value || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null;
}

const DOC_TYPES = { passport: "PASOR", id_card: "IDENT", driving_licence: "PATEN" };

export function createResolver({ comuni, stati, documenti, iso }) {
  const statoByCode = new Map(stati.map(([code, name, iso3, validTo]) => [code, { code, name, iso3, validTo }]));
  const activeStati = stati.filter((s) => !s[3]);
  const statoByName = new Map(activeStati.map(([code, name]) => [plain(name), code]));

  // Nombres de país en los seis idiomas de la app (Intl), para «Deutschland»,
  // «Spain», «Italia»… cuando no hay código.
  const namesToIso2 = new Map();
  const iso2List = Object.keys(iso.by_iso2 || {});
  for (const lang of ["it", "en", "es", "de", "fr", "pt"]) {
    let dn = null;
    try {
      dn = new Intl.DisplayNames([lang], { type: "region" });
    } catch (_) {
      dn = null;
    }
    if (!dn) continue;
    for (const c of iso2List) {
      const n = dn.of(c === "UK" ? "GB" : c);
      if (n && n !== c) namesToIso2.set(plain(n), c);
    }
  }

  const comuniByName = new Map();
  const comuneByCode = new Map();
  for (const [code, name, province, validTo] of comuni) {
    comuneByCode.set(code, { code, name, province, validTo });
    const key = plain(name);
    if (!comuniByName.has(key)) comuniByName.set(key, []);
    comuniByName.get(key).push({ code, name, province, validTo });
  }

  function countryFromCode(value) {
    const c = String(value || "").trim().toUpperCase();
    if (!c) return null;
    if (Object.prototype.hasOwnProperty.call(iso.mrz || {}, c)) return iso.mrz[c] || null;
    return iso.by_iso3?.[c] || iso.by_iso2?.[c] || (statoByCode.has(c) ? c : null);
  }

  function countryFromName(value) {
    const n = plain(value);
    if (!n) return null;
    if (statoByName.has(n)) return statoByName.get(n);
    const iso2 = namesToIso2.get(n);
    return iso2 ? iso.by_iso2[iso2] || null : null;
  }

  function country(value) {
    return countryFromCode(value) || countryFromName(value);
  }

  // «ROMA (RM)», «COMUNE DI MILANO», «Questura di Roma» → comuni candidatos.
  // `asOf` (dd/mm/aaaa): un comune suprimido sigue valiendo para quien nació antes.
  function comune(value, asOf) {
    let text = String(value || "");
    const prov = (text.match(/\(([A-Za-z]{2})\)/) || [])[1];
    text = text.replace(/\([^)]*\)/g, " ");
    const n = plain(text)
      .replace(/^(COMUNE|CITTA|QUESTURA|PREFETTURA|MUNICIPIO)( DI| DE)? /, "")
      .replace(/ ?- ?ITALIA$| ITALIA$/, "")
      .trim();
    if (!n) return { match: null, candidates: [] };
    let cands = comuniByName.get(n) || [];
    if (prov) cands = cands.filter((c) => c.province === prov.toUpperCase());
    const at = itDateToTime(asOf) ?? Date.now();
    const valid = cands.filter((c) => !c.validTo || itDateToTime(c.validTo) >= at);
    const pool = valid.length ? valid : cands;
    return { match: pool.length === 1 ? pool[0] : null, candidates: pool };
  }

  function stato(code) {
    return statoByCode.get(code) || null;
  }

  const documento = new Map(documenti.map(([code, name]) => [code, name]));

  const comuneName = (code) => comuneByCode.get(code)?.name || null;

  return { country, countryFromCode, comune, comuneName, stato, documento, activeStati, documenti };
}

const field = (fields, name) => {
  const f = (fields || []).find((x) => x?.name === name);
  const v = f?.value;
  return v === null || v === undefined || v === "" ? null : v;
};

// Datos de un resultado de la plantilla de DNI/pasaporte → valores y códigos de Alloggiati.
// Devuelve { values, errors: [campo], warnings: [texto] }. `overrides` son las
// correcciones que el usuario hace en la pantalla (mismas claves que `values`).
export function resolveGuest(fields, resolver, overrides = {}) {
  const errors = [];
  const warnings = [];
  const v = {};

  const citt = overrides.cittadinanza
    ? overrides.cittadinanza
    : resolver.countryFromCode(field(fields, "nationality_code")) ||
      resolver.country(field(fields, "nationality")) ||
      null;
  const germanic = GERMANIC.has(citt);

  const full = String(field(fields, "full_name") || "");
  v.cognome = plain(overrides.cognome ?? field(fields, "surname") ?? "", { germanic });
  v.nome = plain(overrides.nome ?? field(fields, "given_names") ?? "", { germanic });
  if (!v.cognome && full) warnings.push("name_split");
  if (!v.cognome) errors.push("cognome");
  if (!v.nome) errors.push("nome");

  const sex = String(overrides.sesso ?? field(fields, "sex") ?? "").toUpperCase();
  v.sesso = sex === "M" || sex === "1" ? "1" : sex === "F" || sex === "2" ? "2" : "";
  if (!v.sesso) errors.push("sesso");

  v.nascita = toItalianDate(overrides.nascita ?? field(fields, "birth_date"));
  if (!v.nascita) errors.push("nascita");

  // Lugar de nacimiento: comune italiano si lo es; si no, estado.
  const place = overrides.luogoNascita ?? field(fields, "place_of_birth");
  v.comuneNascita = "";
  v.provinciaNascita = "";
  v.statoNascita = "";
  if (overrides.comuneNascita) {
    const c = resolver.comune(overrides.comuneNascita, v.nascita);
    if (c.match) {
      v.comuneNascita = c.match.code;
      v.provinciaNascita = c.match.province;
      v.statoNascita = ITALIA;
    }
  }
  if (!v.statoNascita && overrides.statoNascita) v.statoNascita = overrides.statoNascita;
  if (!v.statoNascita && place) {
    const c = resolver.comune(place, v.nascita);
    const inParens = (String(place).match(/\(([^)]{3,})\)/) || [])[1];
    const asCountry = resolver.country(inParens || place);
    if (c.match && (!asCountry || asCountry === ITALIA)) {
      v.comuneNascita = c.match.code;
      v.provinciaNascita = c.match.province;
      v.statoNascita = ITALIA;
    } else if (asCountry && asCountry !== ITALIA) {
      v.statoNascita = asCountry;
    } else if (c.candidates.length > 1) {
      errors.push("comuneNascita");
    }
  }
  v.cittadinanza = citt || "";
  if (!v.cittadinanza) errors.push("cittadinanza");
  if (!v.statoNascita && !errors.includes("comuneNascita")) {
    // Pasaportes y DNI extranjeros casi nunca imprimen el país de nacimiento, solo
    // la ciudad: se propone el de la ciudadanía y se avisa para que lo revise.
    if (v.cittadinanza && v.cittadinanza !== ITALIA) {
      v.statoNascita = v.cittadinanza;
      warnings.push("stato_from_citizenship");
    } else {
      errors.push(v.cittadinanza === ITALIA ? "comuneNascita" : "statoNascita");
    }
  }

  const docType = overrides.tipoDocumento ?? DOC_TYPES[String(field(fields, "document_type") || "").toLowerCase()] ?? "";
  v.tipoDocumento = resolver.documento.has(docType) ? docType : "";
  if (!v.tipoDocumento) errors.push("tipoDocumento");

  v.numeroDocumento = plain(overrides.numeroDocumento ?? field(fields, "document_number") ?? "").replace(/[\s'-]/g, "");
  if (!v.numeroDocumento) errors.push("numeroDocumento");

  // Lugar de expedición: comune si el documento es italiano, estado emisor si no.
  v.luogoRilascio = "";
  if (overrides.luogoRilascio) {
    v.luogoRilascio = overrides.luogoRilascio;
  } else {
    const issuer =
      resolver.countryFromCode(field(fields, "issuing_country")) ||
      resolver.country(field(fields, "issuing_country")) ||
      null;
    if (issuer && issuer !== ITALIA) {
      v.luogoRilascio = issuer;
    } else if (issuer === ITALIA || (!issuer && v.cittadinanza === ITALIA)) {
      const c = resolver.comune(field(fields, "issuing_place"));
      if (c.match) v.luogoRilascio = c.match.code;
    } else if (v.cittadinanza) {
      v.luogoRilascio = v.cittadinanza;
      warnings.push("issuer_from_citizenship");
    }
  }
  if (!v.luogoRilascio) errors.push("luogoRilascio");

  return { values: v, errors, warnings };
}

// Una línea del fichero. `tipo`: uno de TIPO; `arrival`: dd/mm/aaaa; `days`: 1-30.
export function buildLine(v, { tipo, arrival, days }) {
  const withDocument = tipo === TIPO.single || tipo === TIPO.familyHead || tipo === TIPO.groupHead;
  const line =
    pad(tipo, 2) +
    pad(arrival, 10) +
    String(days).padStart(2, "0") +
    pad(v.cognome, 50) +
    pad(v.nome, 30) +
    pad(v.sesso, 1) +
    pad(v.nascita, 10) +
    pad(v.comuneNascita, 9) +
    pad(v.provinciaNascita, 2) +
    pad(v.statoNascita, 9) +
    pad(v.cittadinanza, 9) +
    (withDocument ? pad(v.tipoDocumento, 5) + pad(v.numeroDocumento, 20) + pad(v.luogoRilascio, 9) : " ".repeat(34));
  if (line.length !== 168) throw new Error(`Alloggiati: línea de ${line.length} caracteres`);
  return line;
}

// Errores que impiden generar la línea según el tipo: a familiares y miembros de
// grupo no se les pide documento.
export function blockingErrors(errors, tipo) {
  const withDocument = tipo === TIPO.single || tipo === TIPO.familyHead || tipo === TIPO.groupHead;
  return withDocument
    ? errors
    : errors.filter((e) => !["tipoDocumento", "numeroDocumento", "luogoRilascio"].includes(e));
}

// Tipo de cada huésped según el modo: todos sueltos, o el primero cabeza de familia
// o de grupo y el resto miembros.
export function tiposFor(mode, count) {
  return Array.from({ length: count }, (_, i) => {
    if (mode === "family") return i === 0 ? TIPO.familyHead : TIPO.familyMember;
    if (mode === "group") return i === 0 ? TIPO.groupHead : TIPO.groupMember;
    return TIPO.single;
  });
}

export function buildFile(lines) {
  return lines.join("\r\n");
}
